import styled from 'styled-components';
import { Breakpoints, FontWeight } from '../../../styled/constants';
import { FlexBetween, FlexCenterAligned } from '../../../styled/common';

export const Container = styled.div`
  max-width: ${Breakpoints.desktop}px;
  position: relative;
  padding: 0;
  margin: 0 -15px;
`;

export const Section = styled.section`
  width: 100%;
  color: ${({ theme }) => theme.colors.text.white};
`;

export const DeckStage = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
`;

export const DeckTrack = styled.div<{
  $withTransition: boolean;
  $usePx?: boolean;
  $txPx?: number;
}>`
  display: flex;
  width: 300%;
  will-change: transform;
  transform: translateX(-33.3333%);

  @media (max-width: ${Breakpoints.tablet}px) {
    gap: ${p => (p.$usePx ? '12px' : '0')};
    transform: ${p =>
      p.$usePx ? `translateX(${p.$txPx ?? 0}px)` : 'translateX(-33.3333%)'};
    transition: ${p =>
      p.$withTransition ? 'transform 260ms cubic-bezier(.22,1,.36,1)' : 'none'};
  }
`;

export const Page = styled.div<{ $slidePx?: number }>`
  flex: 0 0 33.3333%;
  margin: 0;
  display: flex;
  box-sizing: border-box;
  min-width: 0;

  @media (min-width: ${Breakpoints.desktop}px) {
    height: 515px;
  }

  @media (max-width: ${Breakpoints.desktop}px) {
    flex-direction: column;
  }

  @media (max-width: ${Breakpoints.tablet}px) {
    flex: 0 0 ${({ $slidePx }) => ($slidePx ? `${$slidePx}px` : '100%')};
    flex-direction: column;
    min-height: 745px;
  }
`;

export const ContentWrapper = styled(FlexBetween)`
  align-items: stretch;
  margin: 0 auto;
  min-height: 515px;
  touch-action: pan-y;
  -ms-touch-action: pan-y;

  @media (max-width: ${Breakpoints.desktop}px) {
    flex-direction: column;
  }
`;

export const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;

  @media (min-width: ${Breakpoints.desktop}) {
    max-width: 833px;
  }

  @media (max-width: ${Breakpoints.tablet}px) {
    width: auto;
    min-width: 0;
  }
`;

export const MoreButton = styled.a`
  display: inline-block;
  margin-top: auto;
  background: ${({ theme }) => theme.colors.text.white};
  color: ${({ theme }) => theme.colors.systemColors.cocoaBrown};
  border-radius: 14px;
  padding: 16px 0;
  text-align: center;
  font-size: 16px;
  font-weight: ${FontWeight.thin};
  text-decoration: none;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.systemColors.blue};
    color: ${({ theme }) => theme.colors.text.white};
  }
`;

export const AdvTitleNavRow = styled(FlexBetween)`
  gap: 80px;
  width: 100%;
  @media (max-width: ${Breakpoints.smallDesktop}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const ButtonWrapper = styled(FlexCenterAligned)`
  gap: 8px;
`;

export const AdvContainer = styled(FlexBetween)`
  width: 100%;
  padding-bottom: 26px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primaryReverse};
  margin-bottom: 40px;
  position: relative;

  @media (max-width: ${Breakpoints.tablet}px) {
    flex-direction: column;
    padding-bottom: 16px;
    align-items: flex-start;

    border-bottom: none;

    &::after {
      content: '';
      position: absolute;
      left: 15px;
      right: 15px;
      bottom: 0;
      height: 1px;
      background: ${({ theme }) => theme.colors.border.primaryReverse};
      pointer-events: none;
    }
  }
`;

export const AdvSliderTrack = styled.div<{ translate: number }>`
  display: flex;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(${({ translate }) => translate}%);
  height: 100%;
`;

export const AdvSlide = styled.div<{ background: string }>`
  flex: 0 0 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 415px;
  height: 100%;
  width: 100%;
  position: relative;
  border-bottom-left-radius: 16px;
  border-top-left-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  background-image: ${({ background }) => `url(${background})`};
  background-size: cover;
  background-position: center;

  @media (max-width: ${Breakpoints.desktop}px) {
    border-bottom-left-radius: 0;
    border-top-right-radius: 16px;
  }

  @media (max-width: ${Breakpoints.tablet}px) {
    min-height: 220px;
  }
`;

export const AdvArrowWrapper = styled.div`
  display: flex;
  gap: 16px;
  position: absolute;
  top: 50%;
  left: 30px;
  right: 30px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 2;

  @media (max-width: ${Breakpoints.desktop}px) {
    display: none;
  }

  @media (max-width: ${Breakpoints.tablet}px) {
    pointer-events: auto;
    display: flex;
    left: 5px;
    right: 5px;
  }
`;

export const AdvArrow = styled.button<{ right?: boolean; visible?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(205, 205, 205, 0.6);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;
  position: absolute;
  top: 50%;
  left: ${({ right }) => (right ? 'auto' : '0')};
  right: ${({ right }) => (right ? '0' : 'auto')};
  transform: translateY(-50%);
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.3s;
  z-index: 5;
  -webkit-tap-highlight-color: transparent;

  &::before {
    content: '';
    width: 16px;
    height: 16px;
    background-image: url('/static/images/pasha/swipe-icon-white.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    display: block;
    transform: ${({ right }) => (right ? 'rotate(180deg)' : 'none')};
  }

  @media (max-width: ${Breakpoints.tablet}px) {
    opacity: 1;
    pointer-events: auto;
    width: 28px;
    height: 28px;

    &::before {
      width: 11px;
      height: 11px;
    }
  }
`;

export const AdvIndicators = styled.div`
  display: flex;
  width: 95%;
  margin: 0 auto;
  gap: 8px;
  position: absolute;
  bottom: 25px;
  left: 0;
  right: 0;
  z-index: 3;
  justify-content: center;
  @media (max-width: 1200px) {
    bottom: 25px;
  }
  @media (max-width: 768px) {
    bottom: 5px;
  }
`;

export const AdvIndicator = styled.button<{
  active: boolean;
  autoPlay: boolean;
}>`
  flex-grow: ${({ active }) => (active ? 2 : 1)};
  height: 5px;
  border-radius: 10px;
  background-color: ${({ active, theme, autoPlay }) =>
    active && !autoPlay
      ? theme.colors.text.primary
      : 'rgba(225, 225, 225, 0.5)'};
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border: none;
  outline: none;
  padding: 0;
  .progress {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    background-color: ${({ theme }) => theme.colors.text.primary};
    animation: progressAnim 10s linear forwards;
    border-radius: inherit;
    pointer-events: none;
  }
  &:hover {
    background: rgba(225, 225, 225, 0.8);
  }
  @keyframes progressAnim {
    from {
      width: 0%;
    }
    to {
      width: 100%;
    }
  }
`;

export const ScrollArrow = styled.button<{ right?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  background-color: rgba(210, 210, 210, 0.5);
  backdrop-filter: blur(5px);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;

  &::before {
    content: '';
    width: 16px;
    height: 16px;
    background-image: url('/static/images/pasha/swipe-icon-white.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    display: block;
    transform: ${({ right }) => (right ? 'rotate(180deg)' : 'none')};
  }

  &:disabled {
    opacity: 0;
    pointer-events: none;
  }
`;

export const ScrollArrowLeft = styled(ScrollArrow)`
  left: 5px;
`;

export const ScrollArrowRight = styled(ScrollArrow)`
  right: 5px;
`;
